import { useState } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';

// Wipes all Service Worker caches + unregisters the service worker so the
// next page load fetches everything fresh from the server. Preserves
// localStorage (so admin sessions and saved preferences survive) but purges
// the in-browser PWA cache which is what causes "I don't see my latest update"
// confusion in deployed PWAs.
async function forceRefreshApp() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // noop — some browsers (older Safari) disable caches API in private mode
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // noop
  }

  // Bypass bfcache: append a cache-buster to the URL before reload.
  const url = new URL(window.location.href);
  url.searchParams.set('v', Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Renders a small button that clears the PWA cache and reloads. Props:
 *  - `variant`: 'footer' | 'pill' | 'icon' (default: 'footer')
 *  - `className`: extra classes
 *  - `label`: override the default button text
 */
export default function ForceRefreshButton({ variant = 'footer', className = '', label }) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (busy) return;
    const ok = window.confirm(
      'This will clear the app cache and reload the page to show the very latest updates. Your admin login and local settings will be preserved. Continue?'
    );
    if (!ok) return;
    setBusy(true);
    await forceRefreshApp();
  };

  const text = label || (busy ? 'Refreshing…' : 'Clear cache & refresh');
  const Icon = busy ? Loader2 : RefreshCw;

  if (variant === 'icon') {
    return (
      <button
        type="button"
        data-testid="force-refresh-btn"
        onClick={onClick}
        disabled={busy}
        title="Clear cache & refresh"
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50 ${className}`}
        style={{ backgroundColor: '#A3D4D420', color: '#1F4FBE' }}
      >
        <Icon className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
      </button>
    );
  }

  if (variant === 'pill') {
    return (
      <button
        type="button"
        data-testid="force-refresh-btn"
        onClick={onClick}
        disabled={busy}
        className={`flex items-center gap-2 rounded-full font-bold transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 ${className || 'text-xs px-3 py-1.5'}`}
        style={{ backgroundColor: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}
      >
        <Icon className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
        {text}
      </button>
    );
  }

  // footer variant: subtle text link
  return (
    <button
      type="button"
      data-testid="force-refresh-btn"
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold transition-colors hover:text-white disabled:opacity-50 ${className}`}
      style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}
    >
      <Icon className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
      {text}
    </button>
  );
}
